import type { FC } from 'react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

type WebViewSource = { uri: string } | { html: string };

export type WebViewProps = {
  source?: WebViewSource;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  /**
   * Maps to iframe `allow` attribute.
   * Example: "camera; microphone; fullscreen"
   */
  allow?: string;
  /**
   * Maps to iframe `sandbox` attribute.
   * Example: "allow-scripts allow-same-origin"
   */
  sandbox?: string;
  /**
   * Maps to iframe `referrerPolicy` attribute.
   * Example: "no-referrer"
   */
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  onLoad?: () => void;
  onError?: (event: unknown) => void;
};

/**
 * Hide the scroll owner's native scrollbar — we draw our own always-visible thumb
 * instead (see below). iOS Safari does NOT honour `::-webkit-scrollbar` styling and
 * always uses an auto-hiding overlay bar, so a persistent native scrollbar is
 * impossible there; a custom thumb is the only cross-platform way to always show it.
 */
const HIDE_SCROLLBAR_CLASS = 'usk-webview-hide-scrollbar';

function ensureHideScrollbarStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HIDE_SCROLLBAR_CLASS)) return;
  const styleEl = document.createElement('style');
  styleEl.id = HIDE_SCROLLBAR_CLASS;
  styleEl.textContent = `.${HIDE_SCROLLBAR_CLASS}::-webkit-scrollbar{width:0;height:0;display:none}`;
  document.head.appendChild(styleEl);
}

const THUMB_MIN_HEIGHT = 24;
const THUMB_INSET = 2;

type Thumb = { top: number; height: number };

const WebViewComponent: FC<WebViewProps> = ({
  source,
  style,
  testID,
  allow,
  sandbox,
  referrerPolicy,
  onLoad,
  onError,
}) => {
  const resolved = useMemo(() => {
    if (!source) return { kind: 'none' as const };
    if ('uri' in source) return { kind: 'uri' as const, uri: source.uri };
    return { kind: 'html' as const, html: source.html };
  }, [source]);

  const isHtml = resolved.kind === 'html';

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentObserverRef = useRef<ResizeObserver | null>(null);
  const wrapperObserverRef = useRef<ResizeObserver | null>(null);
  const rafRef = useRef<number | null>(null);
  // For srcDoc (same-origin) content we grow the iframe to its full content height and
  // let the wrapping div scroll instead. iOS Safari ignores overflow/height on an iframe
  // and always sizes it to its content, so an internal scroll never happens — the content
  // just overflowed the (clipped) modal card and the bottom was unreachable.
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  // Our custom, always-visible scroll indicator. `null` = content fits (nothing to show).
  const [thumb, setThumb] = useState<Thumb | null>(null);

  const measure = useCallback(() => {
    const el = iframeRef.current;
    if (!el) return;
    try {
      const doc = el.contentDocument;
      const body = doc?.body;
      const root = doc?.documentElement;
      const height = Math.max(root?.scrollHeight ?? 0, body?.scrollHeight ?? 0);
      if (height > 0) setContentHeight(height);
    } catch {
      // Cross-origin (uri) documents aren't readable; leave height at 100%.
    }
  }, []);

  // Recompute the thumb size/position from the scroll owner's current metrics.
  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflow = scrollHeight - clientHeight;
    if (overflow <= 1 || clientHeight <= 0) {
      setThumb(null);
      return;
    }
    const trackHeight = clientHeight - THUMB_INSET * 2;
    const height = Math.max(THUMB_MIN_HEIGHT, (clientHeight / scrollHeight) * trackHeight);
    const top = THUMB_INSET + (scrollTop / overflow) * (trackHeight - height);
    setThumb({ top, height });
  }, []);

  // Coalesce scroll bursts to one update per frame.
  const scheduleUpdateThumb = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updateThumb();
    });
  }, [updateThumb]);

  const handleLoad = useCallback(() => {
    if (isHtml) {
      measure();
      // Re-measure + re-draw the thumb on reflow (font swap, image load, orientation/width).
      try {
        const win = iframeRef.current?.contentWindow;
        const body = iframeRef.current?.contentDocument?.body;
        if (win && body && 'ResizeObserver' in win) {
          contentObserverRef.current?.disconnect();
          const RO = (win as unknown as { ResizeObserver: typeof ResizeObserver }).ResizeObserver;
          contentObserverRef.current = new RO(() => {
            measure();
            scheduleUpdateThumb();
          });
          contentObserverRef.current.observe(body);
        }
      } catch {
        // Best-effort; the initial measure already covers the common case.
      }
    }
    onLoad?.();
  }, [isHtml, measure, scheduleUpdateThumb, onLoad]);

  // Redraw when the measured content height changes (iframe just grew/shrank).
  useEffect(() => {
    scheduleUpdateThumb();
  }, [contentHeight, scheduleUpdateThumb]);

  // Redraw when the scroll owner itself resizes (modal open, rotation, split-view).
  useEffect(() => {
    ensureHideScrollbarStyle();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    wrapperObserverRef.current = new ResizeObserver(() => scheduleUpdateThumb());
    wrapperObserverRef.current.observe(el);
    return () => wrapperObserverRef.current?.disconnect();
  }, [scheduleUpdateThumb]);

  useEffect(
    () => () => {
      contentObserverRef.current?.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const iframeStyle = useMemo<React.CSSProperties>(
    () => ({
      border: 0,
      width: '100%',
      display: 'block',
      height: isHtml ? (contentHeight != null ? `${contentHeight}px` : 'auto') : '100%',
    }),
    [isHtml, contentHeight],
  );

  // The scroll owner. `WebkitOverflowScrolling: touch` gives iOS Safari momentum
  // scrolling on this real element (which it handles, unlike the iframe itself).
  const scrollWrapperStyle = useMemo<React.CSSProperties>(
    () => ({
      width: '100%',
      height: '100%',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      // Native bar hidden — the custom thumb below is the visible indicator.
      scrollbarWidth: 'none',
    }),
    [],
  );

  if (resolved.kind === 'none') return null;

  // RN web renders <View> as a div; iframe needs CSS sizing.
  // `Platform.OS` guard is defensive; this file should only be used on web.
  if (Platform.OS !== 'web') return null;

  return (
    <View style={[styles.container, style]} testID={testID}>
      <div
        ref={scrollRef}
        className={HIDE_SCROLLBAR_CLASS}
        style={scrollWrapperStyle}
        onScroll={scheduleUpdateThumb}>
        <iframe
          ref={iframeRef}
          title={testID ?? 'webview'}
          src={resolved.kind === 'uri' ? resolved.uri : undefined}
          srcDoc={resolved.kind === 'html' ? resolved.html : undefined}
          style={iframeStyle}
          // The wrapper scrolls for srcDoc content, so stop the iframe from double-scrolling.
          scrolling={isHtml ? 'no' : undefined}
          allow={allow}
          sandbox={sandbox}
          referrerPolicy={referrerPolicy}
          onLoad={handleLoad}
          onError={e => onError?.(e)}
        />
      </div>
      {isHtml && thumb ? (
        <View
          pointerEvents="none"
          style={[styles.thumb, { top: thumb.top, height: thumb.height }]}
        />
      ) : null}
    </View>
  );
};

export const WebView = memo(WebViewComponent);

WebView.displayName = 'WebView';

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  // eslint-disable-next-line react-native/no-color-literals
  thumb: {
    position: 'absolute',
    right: THUMB_INSET,
    width: 3,
    borderRadius: 3,
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
});
